import React, { useEffect, useRef, useState } from 'react';
import './ParticleSystem.css';

const ParticleSystem = () => {
  const containerRef = useRef(null);
  const [particles, setParticles] = useState([]);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [gradientColors, setGradientColors] = useState({
    color1: { h: 240, s: 100, l: 50 },
    color2: { h: 300, s: 80, l: 60 },
    color3: { h: 180, s: 90, l: 55 }
  });

  // Generate random particles
  useEffect(() => {
    const generateParticles = () => {
      const newParticles = [];
      const particleCount = 50;

      for (let i = 0; i < particleCount; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 6 + 2,
          speedX: (Math.random() - 0.5) * 0.5,
          speedY: (Math.random() - 0.5) * 0.5,
          opacity: Math.random() * 0.8 + 0.2,
          hue: Math.random() * 360,
          pulseSpeed: Math.random() * 2 + 1
        });
      }
      setParticles(newParticles);
    };

    generateParticles();
  }, []);

  // Animate particles with requestAnimationFrame for better performance
  useEffect(() => {
    let animationId;
    let lastTime = 0;
    
    const animateParticles = (currentTime) => {
      // Throttle to ~60 FPS
      if (currentTime - lastTime >= 16) {
        setParticles(prevParticles =>
          prevParticles.map(particle => {
            let newX = particle.x + particle.speedX;
            let newY = particle.y + particle.speedY;

            // Bounce off edges with smooth damping
            if (newX <= 0 || newX >= 100) {
              particle.speedX *= -0.8; // Add some damping
              newX = Math.max(0, Math.min(100, newX));
            }
            if (newY <= 0 || newY >= 100) {
              particle.speedY *= -0.8; // Add some damping
              newY = Math.max(0, Math.min(100, newY));
            }

            return {
              ...particle,
              x: newX,
              y: newY,
              hue: (particle.hue + 0.3) % 360 // Slowly shift hue
            };
          })
        );
        lastTime = currentTime;
      }
      
      animationId = requestAnimationFrame(animateParticles);
    };

    animationId = requestAnimationFrame(animateParticles);
    return () => cancelAnimationFrame(animationId);
  }, []);

  // Track mouse movement and add click interactions
  useEffect(() => {
    const handleMouseMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePos({ x, y });

        // Update gradient colors based on mouse position
        setGradientColors({
          color1: {
            h: (x * 3.6) % 360,
            s: 70 + (y * 0.3),
            l: 40 + (x * 0.2)
          },
          color2: {
            h: ((x * 3.6) + 120) % 360,
            s: 80 + (x * 0.2),
            l: 50 + (y * 0.3)
          },
          color3: {
            h: ((x * 3.6) + 240) % 360,
            s: 90 - (y * 0.2),
            l: 60 + (x * 0.1)
          }
        });
      }
    };

    const handleClick = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const clickX = ((e.clientX - rect.left) / rect.width) * 100;
        const clickY = ((e.clientY - rect.top) / rect.height) * 100;
        
        // Add burst of particles at click location
        const burstParticles = [];
        for (let i = 0; i < 10; i++) {
          burstParticles.push({
            id: Date.now() + i,
            x: clickX + (Math.random() - 0.5) * 5,
            y: clickY + (Math.random() - 0.5) * 5,
            size: Math.random() * 8 + 3,
            speedX: (Math.random() - 0.5) * 2,
            speedY: (Math.random() - 0.5) * 2,
            opacity: Math.random() * 0.9 + 0.1,
            hue: Math.random() * 360,
            pulseSpeed: Math.random() * 1.5 + 0.5,
            lifespan: 100 // Temporary particles
          });
        }
        
        setParticles(prev => [...prev, ...burstParticles]);
        
        // Remove burst particles after animation
        setTimeout(() => {
          setParticles(prev => 
            prev.filter(p => !burstParticles.some(bp => bp.id === p.id))
          );
        }, 3000);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
    };
  }, []);

  const gradientStyle = {
    background: `
      radial-gradient(circle at ${mousePos.x}% ${mousePos.y}%, 
        hsla(${gradientColors.color1.h}, ${gradientColors.color1.s}%, ${gradientColors.color1.l}%, 0.8) 0%,
        hsla(${gradientColors.color2.h}, ${gradientColors.color2.s}%, ${gradientColors.color2.l}%, 0.6) 50%,
        hsla(${gradientColors.color3.h}, ${gradientColors.color3.s}%, ${gradientColors.color3.l}%, 0.4) 100%
      ),
      linear-gradient(45deg, 
        hsla(${(gradientColors.color1.h + 60) % 360}, 60%, 20%, 0.9),
        hsla(${(gradientColors.color2.h + 60) % 360}, 70%, 30%, 0.7)
      )
    `
  };

  return (
    <div 
      ref={containerRef}
      className="particle-container"
      style={gradientStyle}
    >
      {particles.map(particle => (
        <div
          key={particle.id}
          className="particle"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            opacity: particle.opacity,
            background: `hsla(${particle.hue}, 80%, 70%, 0.8)`,
            boxShadow: `0 0 ${particle.size * 2}px hsla(${particle.hue}, 100%, 80%, 0.6)`,
            animationDuration: `${particle.pulseSpeed}s`
          }}
        />
      ))}
      
      {/* Add some floating orbs for extra magic */}
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>
      <div className="orb orb-3"></div>
    </div>
  );
};

export default ParticleSystem;